const IOS_URL = 'https://apps.apple.com/app/storychain-fun/id6756502690';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=fun.storychain.mobile';

interface AppStoreBadgesProps {
  /** Stack vertically (default) or lay out in a single row. */
  layout?: 'stack' | 'row';
  className?: string;
}

/**
 * App Store + Google Play buttons. Used both inside the GetAppModal (stack
 * layout) and inline at the end of shared story/comic pages (row layout
 * for more compact presentation).
 */
export const AppStoreBadges: React.FC<AppStoreBadgesProps> = ({ layout = 'stack', className }) => {
  const container = layout === 'row' ? 'flex flex-wrap gap-3' : 'space-y-3';
  return (
    <div className={`${container} ${className ?? ''}`.trim()}>
      <a
        href={IOS_URL}
        target="_blank"
        rel="noreferrer"
        className="flex-1 min-w-[160px] flex items-center justify-center gap-2 bg-black text-white py-3 px-4 rounded-xl font-semibold hover:bg-gray-800 transition"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
        <span className="whitespace-nowrap">App Store (iOS)</span>
      </a>
      <a
        href={ANDROID_URL}
        target="_blank"
        rel="noreferrer"
        className="flex-1 min-w-[160px] flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-green-700 transition"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.523 15.34c-.552 0-1-.447-1-1s.448-1 1-1 1 .447 1 1-.448 1-1 1zm-11.046 0c-.553 0-1-.447-1-1s.447-1 1-1 1 .447 1 1-.447 1-1 1zm11.405-6.082l1.997-3.458a.416.416 0 0 0-.152-.568.416.416 0 0 0-.568.152l-2.022 3.503C15.593 8.15 13.85 7.7 12 7.7c-1.85 0-3.593.45-5.138 1.187L4.84 5.384a.416.416 0 0 0-.568-.152.416.416 0 0 0-.152.568l1.997 3.458C2.688 11.075.366 14.515 0 18.5h24c-.366-3.985-2.688-7.425-6.118-9.242z" />
        </svg>
        <span className="whitespace-nowrap">Google Play (Android)</span>
      </a>
    </div>
  );
};

interface AppStorePromptCardProps {
  /** Short prompt shown above the buttons. */
  message?: string;
  className?: string;
}

/**
 * Inline card inviting the reader to grab the mobile app. Replaces the
 * bare "Download StoryChain App →" links at the bottom of shared pages.
 */
export const AppStorePromptCard: React.FC<AppStorePromptCardProps> = ({
  message = 'Want to create your own? Grab the app.',
  className,
}) => {
  return (
    <div className={`bg-white rounded-2xl p-5 shadow-sm text-center ${className ?? ''}`.trim()}>
      <p className="text-gray-600 mb-3">{message}</p>
      <AppStoreBadges layout="row" />
    </div>
  );
};
